import React, { useEffect, useState, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { TpmBar, TpmPie } from "../../home/chartKit";
import { HOME_SCALE } from "../../home/homeTheme";
import "./VisitDataSummary.scss";
import calendarIcon from "../../../../Assets/Images/iconCalendar.png";
import { httpService } from "../../../../services/httpService.service";
import dayjs from "dayjs";
import Filters from "./Filters";
import { Button } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { isAuthorized } from "../../../../services/authService";
import { RolesEnum } from "../../../../services/rolesEnum";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const VisitsPlanningExecution = () => {
  const isGuestShare = process.env.REACT_APP_API_IS_GUEST;
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    state: "",
    country: "",
    startDate: "",
    endDate: "",
  });

  const chartRef = useRef(null);

  const fetchData = () => {
    const projectId = localStorage.getItem("currentProjectID");

    const startDate = localStorage.getItem("Contract-startDate");
    const endDate = localStorage.getItem("Contract-endDate");

    const params = {
      ProjectId: projectId,
      StartDate: filters.startDate || dayjs(startDate).startOf("month"),
      EndDate: filters.endDate || dayjs(endDate).endOf("month"),
      Country: filters.country,
      State: filters.state,
    };

    const queryString = new URLSearchParams(params).toString();

    httpService.get(
      `/api/Site/visitPlanningExecution?${queryString}`,
      (res) => {
        setData(res.data);
      },
      (err) => {
        // console.log(err);
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const plannedVisits = data?.visitPlanned || 0;
  const completedVisits = data?.visitCompleted || 0;

  const completedPercentage =
    plannedVisits > 0 ? (completedVisits / plannedVisits) * 100 : 0;
  const hasVisitData = plannedVisits > 0;

  const verticalBarData = {
    labels: ["Visits"],
    datasets: [
      {
        label: "Visits Planned",
        data: hasVisitData ? [100] : [1],
        backgroundColor: hasVisitData ? HOME_SCALE.critical : "rgba(0,0,0,0.1)",
        borderColor: hasVisitData ? HOME_SCALE.critical : "rgba(0,0,0,0.1)",
        borderWidth: 1,
      },
      {
        label: "Visits Completed",
        data: hasVisitData ? [completedPercentage] : [1],
        backgroundColor: hasVisitData ? HOME_SCALE.good : "rgba(0,0,0,0.1)",
        borderColor: hasVisitData ? HOME_SCALE.good : "rgba(0,0,0,0.1)",
        borderWidth: 1,
      },
    ],
  };

  const verticalBarOptions = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: "Visits Overview",
        font: {
          size: 18,
          weight: "600",
          style: "normal",
          family: "Poppins",
          lineHeight: "26px",
        },
        color: "#6A113C",
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            if (!hasVisitData) return "No Data Available";
            return `${tooltipItem.dataset.label}: ${tooltipItem.raw}%`;
          },
        },
      },
      legend: { position: "bottom" },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: { stepSize: 10, callback: (value) => `${value}%` },
      },
    },
  };

  const hasData =
    (data?.notCompletedWithinDate || 0) > 0 ||
    (data?.completedWithinDate || 0) > 0;
  const pieData = {
    labels: hasData
      ? ["Not completed within planned date", "Completed within planned date"]
      : ["No Data Available"],
    datasets: [
      {
        label: "On-Time Visit Rate",
        data: hasData
          ? [data?.notCompletedWithinDate, data?.completedWithinDate]
          : [1],
        backgroundColor: hasData ? [HOME_SCALE.critical, HOME_SCALE.good] : ["rgba(0,0,0,0.1)"],
        borderColor: hasData ? [HOME_SCALE.critical, HOME_SCALE.good] : ["rgba(0,0,0,0.1)"],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return hasData ? tooltipItem.label : "No Data Available";
          },
        },
      },
      title: {
        display: true,
        text: "On-Time Visit Rate",
        font: {
          size: 18,
          weight: "600",
          style: "normal",
          family: "Poppins",
          lineHeight: "26px",
        },
        color: "#6A113C",
      },
    },
  };

  const handleExportPDF = () => {
    if (!chartRef.current) return;

    toPng(chartRef.current, { cacheBust: true })
      .then((dataUrl) => {
        const pdf = new jsPDF();
        const imgWidth = 180; // Adjust the image width
        const imgHeight =
          (chartRef.current.offsetHeight / chartRef.current.offsetWidth) *
          imgWidth;

        pdf.addImage(dataUrl, "PNG", 15, 15, imgWidth, imgHeight);
        pdf.save("Visits Planning & Execution.pdf");
      })
      .catch((err) => {
        console.error("Error exporting to PDF:", err);
      });
  };

  const handleExportExcel = () => {
    const projectId = localStorage.getItem("currentProjectID");
    const params = {
      projectId: projectId,
      startDate: filters.startDate || dayjs().startOf("month"),
      endDate: filters.endDate || dayjs().endOf("month"),
      ...(filters.state ? { country: filters.state } : {}),
      ipAnalysis: false,
      exportFilters: {
        includeVisitPlanningExecution: true,
      },
    };

    httpService.post(
      `/api/Site/exportVisitDataSummary`,
      params,
      (response) => {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "Visits Planning & Execution.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      },
      (err) => {
        console.error(err);
      },
      { responseType: "arraybuffer" }
    );
  };

  return (
    <>
      <div className="export-container row gap-2">
        <div className="col-12 col-lg-8">
          <Filters filters={filters} setFilters={setFilters} />
        </div>
        {isAuthorized([
          RolesEnum.SystemAdmin,
          RolesEnum.ProjectManager,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldTeamLeader,
        ]) && (
          <div className="row gap-2 col-12 col-lg-4">
            <Button
              className="exportButtonTPM addButtonTPM col-12 col-md-5 col-lg-5"
              style={{
                padding: "5px 20px",
                fontSize: "13px",
                maxHeight: "35px",
              }}
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
            >
              Export to Excel
            </Button>
            <Button
              className="exportButtonTPM addButtonTPM col-12 col-md-5 col-lg-5"
              style={{
                padding: "5px 20px",
                fontSize: "13px",
                maxHeight: "35px",
                background: " var(--2, #6A113C) !important",
              }}
              icon={<DownloadOutlined />}
              onClick={handleExportPDF}
            >
              Export to PDF
            </Button>
          </div>
        )}
      </div>

      <div ref={chartRef} className="charts-container">
        <div className="chart-card">
          <TpmBar data={verticalBarData} options={verticalBarOptions} />
        </div>
        <div className="chart-card">
          <TpmPie data={pieData} options={pieOptions} />
        </div>
        {!isGuestShare && (
          <div className="delays-card">
            <div className="textWrapper">
              <h2>Delays Between Estimated Start and End Day for Each Site</h2>
              <h1 className="">{data?.delayDays} days</h1>
            </div>
            <img src={calendarIcon} alt="calendar" />
          </div>
        )}
      </div>
    </>
  );
};

export default VisitsPlanningExecution;
